import { useMemo, useContext } from 'react';

import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react/conditional-hooks-factory/conditional-hooks-factory';

import { fg } from '@confluence/feature-gating';
import { useIsCompanyHubEnabled } from '@confluence/company-hub-utils/entry-points/useIsCompanyHubEnabled.ts';
import { useViewUserProfile } from '@confluence/global-operations/entry-points/useViewUserProfile.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { SYSTEM_HEADER_LEFT } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { useWebItemLocation } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';

// TODO: there is a large useMenuItems.ts file that might need refactoring, but for the moment lets just duplicate the keys where they are needed
// to avoid coupling the new side navigation code with the old menu item code. We can look at unifying these in the future when we have more clarity on what the new side navigation needs to do.
const SPACE_DIRECTORY_KEY =
	'com.atlassian.confluence.plugins.confluence-space-ia:header-spaces-link';
const PEOPLE_DIRECTORY_KEY =
	'com.atlassian.confluence.plugins.confluence-space-ia:header-people-link';

type Result = {
	isPeopleVisible: boolean;
	isCompanyHubVisible: boolean;
	isJpdVisible: boolean;
};

const useShortcutVisibilityOverridesOld = (): Result => {
	const { loading: webItemsLoading, webItems } = useWebItemLocation({
		location: SYSTEM_HEADER_LEFT,
		allowedWebItems: [SPACE_DIRECTORY_KEY, PEOPLE_DIRECTORY_KEY],
	});
	const { isCompanyHubEntryPointEnabled } = useIsCompanyHubEnabled();
	const { frontCoverState, loading } = useContext(SPAViewContext);
	const { canViewUserProfile: hasViewUserProfilePermission } = useViewUserProfile();

	const isPeopleVisible =
		!webItemsLoading &&
		(hasViewUserProfilePermission ||
			!!webItems.find((item) => item.completeKey === PEOPLE_DIRECTORY_KEY));
	const isCompanyHubVisible = !loading && isCompanyHubEntryPointEnabled && !!frontCoverState;

	return useMemo(
		() => ({
			isPeopleVisible,
			isCompanyHubVisible,
			isJpdVisible: false,
		}),
		[isCompanyHubVisible, isPeopleVisible],
	);
};

const useShortcutVisibilityOverridesNew = (): Result => {
	const { loading: webItemsLoading, webItems } = useWebItemLocation({
		location: SYSTEM_HEADER_LEFT,
		allowedWebItems: [SPACE_DIRECTORY_KEY, PEOPLE_DIRECTORY_KEY],
	});
	const { isCompanyHubEntryPointEnabled } = useIsCompanyHubEnabled();
	const { frontCoverState, loading } = useContext(SPAViewContext);
	const { canViewUserProfile: hasViewUserProfilePermission } = useViewUserProfile();
	const renderServerPlaceholder = useIsSSROrHydrating();

	const isPeopleVisible =
		!webItemsLoading &&
		(hasViewUserProfilePermission ||
			!!webItems.find((item) => item.completeKey === PEOPLE_DIRECTORY_KEY));
	const isCompanyHubVisible = !loading && isCompanyHubEntryPointEnabled && !!frontCoverState;

	// Skip on SSR and render after hydration finished (old nav)
	const isJpdVisible = !renderServerPlaceholder;

	return useMemo(
		() => ({
			isPeopleVisible,
			isCompanyHubVisible,
			isJpdVisible,
		}),
		[isCompanyHubVisible, isJpdVisible, isPeopleVisible],
	);
};

export const useShortcutVisibilityOverrides: () => Result = conditionalHooksFactory(
	() => {
		// Warning: JPD shortcut doesn't work in the new nav (SSR hydration issue).
		// If you're a dev cleaning up navx-global-side-nav-confluence — please fix the hydration if you can,
		// or if you can't — return false here and let the JPD Juno team know that their feature needs to be fixed.
		// Slack channel: #jpd-team-juno (https://atlassian.enterprise.slack.com/archives/C05CRATNGDA)
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		return fg('jpd_try_in_confluence_app_shortcuts_ks') && !fg('navx-global-side-nav-confluence');
	},
	useShortcutVisibilityOverridesNew,
	useShortcutVisibilityOverridesOld,
);
